import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Save, RotateCcw, Trash2, Plus, FileText, AlertCircle } from 'lucide-react';

const EMPTY_ROW = { libelle: '', prix_ht: 0, prix_ttc: 0 };

const AdminTarifsEnedis = () => {
  const [tarifs, setTarifs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/tarifs-enedis');
      setTarifs({
        fta: data.fta || [],
        ps: data.ps || [],
        intro_text: data.intro_text || '',
        footer_note: data.footer_note || '',
        updated_at: data.updated_at || null,
      });
    } catch (e) {
      toast.error("Erreur chargement des tarifs Enedis");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    for (const section of ['fta', 'ps']) {
      for (const [i, row] of (tarifs[section] || []).entries()) {
        if (!row.libelle?.trim()) {
          toast.error(`${section.toUpperCase()} ligne ${i + 1} : libellé obligatoire`);
          return;
        }
        if (Number(row.prix_ht) < 0 || Number(row.prix_ttc) < 0) {
          toast.error(`${section.toUpperCase()} ligne ${i + 1} : prix négatif interdit`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        fta: tarifs.fta.map(r => ({ libelle: r.libelle, prix_ht: Number(r.prix_ht) || 0, prix_ttc: Number(r.prix_ttc) || 0 })),
        ps: tarifs.ps.map(r => ({ libelle: r.libelle, prix_ht: Number(r.prix_ht) || 0, prix_ttc: Number(r.prix_ttc) || 0 })),
        intro_text: tarifs.intro_text,
        footer_note: tarifs.footer_note,
      };
      const { data } = await api.put('/admin/tarifs-enedis', payload);
      setTarifs({ ...data });
      toast.success('Tarifs Enedis sauvegardés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser les tarifs Enedis aux valeurs par défaut ?')) return;
    try {
      const { data } = await api.post('/admin/tarifs-enedis/reset');
      setTarifs({ ...data });
      toast.success('Tarifs réinitialisés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur réinitialisation');
    }
  };

  const updateRow = (section, idx, field, value) => {
    const copy = [...tarifs[section]];
    copy[idx] = { ...copy[idx], [field]: field === 'libelle' ? value : (value === '' ? '' : Number(value)) };
    setTarifs({ ...tarifs, [section]: copy });
  };

  const addRow = (section) => {
    setTarifs({ ...tarifs, [section]: [...tarifs[section], { ...EMPTY_ROW }] });
  };

  const removeRow = (section, idx) => {
    const copy = [...tarifs[section]];
    copy.splice(idx, 1);
    setTarifs({ ...tarifs, [section]: copy });
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-gray-500" data-testid="admin-tarifs-loading">
        Chargement des tarifs Enedis…
      </div>
    );
  }

  if (!tarifs) {
    return (
      <div className="p-8 text-sm text-red-600 flex items-center gap-2" data-testid="admin-tarifs-error">
        <AlertCircle className="w-4 h-4" /> Impossible de charger les tarifs.
      </div>
    );
  }

  const renderSection = (section, title, description) => (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`section-${section}`}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-semibold text-gray-900">{title}</h2>
        <button
          type="button"
          onClick={() => addRow(section)}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100"
          data-testid={`btn-add-${section}`}
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter une ligne
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">{description}</p>
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Type d'intervention</th>
              <th className="text-right px-3 py-2 w-32">Prix HT (€)</th>
              <th className="text-right px-3 py-2 w-32">Prix TTC (€)</th>
              <th className="w-12 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-testid={`tbody-${section}`}>
            {tarifs[section].map((row, idx) => (
              <tr key={`${section}-${idx}`}>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={row.libelle}
                    onChange={e => updateRow(section, idx, 'libelle', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid={`input-${section}-${idx}-libelle`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={row.prix_ht}
                    onChange={e => updateRow(section, idx, 'prix_ht', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-200 rounded text-right font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid={`input-${section}-${idx}-ht`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={row.prix_ttc}
                    onChange={e => updateRow(section, idx, 'prix_ttc', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-200 rounded text-right font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid={`input-${section}-${idx}-ttc`}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => removeRow(section, idx)}
                    className="text-red-500 hover:text-red-700"
                    title="Supprimer cette ligne"
                    data-testid={`btn-remove-${section}-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {tarifs[section].length === 0 && (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400 text-sm">Aucune ligne. Cliquez sur "Ajouter une ligne".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="admin-tarifs-enedis-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Tarifs Enedis — Modification PS / FTA
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Grille tarifaire injectée dans la section "Tarifs Enedis" de tous les rapports TURPE (écran &amp; PDF).
          </p>
          {tarifs.updated_at && (
            <p className="text-xs text-gray-400 mt-1" data-testid="last-updated">
              Dernière mise à jour : {new Date(tarifs.updated_at).toLocaleString('fr-FR')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center gap-1 text-sm px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            data-testid="btn-reset-tarifs"
          >
            <RotateCcw className="w-4 h-4" /> Réinitialiser
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1 text-sm px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            data-testid="btn-save-tarifs"
          >
            <Save className="w-4 h-4" /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Textes explicatifs */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4" data-testid="section-textes">
        <h2 className="font-semibold text-gray-900">Textes explicatifs (rapport TURPE)</h2>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Texte d'introduction (au-dessus des tableaux)</label>
          <textarea
            value={tarifs.intro_text}
            onChange={e => setTarifs({ ...tarifs, intro_text: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            data-testid="input-intro-text"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Note de bas (sous les tableaux)</label>
          <textarea
            value={tarifs.footer_note}
            onChange={e => setTarifs({ ...tarifs, footer_note: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            data-testid="input-footer-note"
          />
        </div>
      </div>

      {renderSection('fta', '1. Modification de Formule Tarifaire d\'Acheminement (FTA)', 'Prestations Enedis facturées pour modifier la FTA d\'un site HTA / BT > 36 kVA.')}
      {renderSection('ps', '2. Modification de la Puissance Souscrite', 'Prestations Enedis facturées pour modifier la puissance souscrite d\'un site HTA / BT > 36 kVA.')}

      <div className="sticky bottom-0 bg-white border-t border-gray-200 py-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1 text-sm px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          data-testid="btn-save-tarifs-bottom"
        >
          <Save className="w-4 h-4" /> {saving ? 'Sauvegarde…' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
};

export default AdminTarifsEnedis;
